/**
 * BETA — achat (purchase) lifecycle handlers.
 */
import { httpError } from "../router.js";
import { paginate, matchSearch, newId, nowIso, todayYmd, dateOffsetYmd, recomputeAchatTotals } from "./_utils.js";

export function register(route) {
  route("GET", "/achats", (ctx) => {
    const { params, store } = ctx;
    let rows = [...store.data.achats];
    if (params.statut) rows = rows.filter((a) => a.statut === params.statut);
    if (params.fournisseur_id) rows = rows.filter((a) => a.fournisseur_id === params.fournisseur_id);
    if (params.date_debut) rows = rows.filter((a) => a.date_facture >= params.date_debut);
    if (params.date_fin) rows = rows.filter((a) => a.date_facture <= params.date_fin);
    if (params.search) {
      rows = rows.filter((a) =>
        matchSearch(a, params.search, ["numero_interne", "numero_piece"]) ||
        matchSearch(a.fournisseur || {}, params.search, ["nom"]),
      );
    }
    if (params.sort_by) {
      const dir = params.sort_order === "asc" ? 1 : -1;
      rows.sort((x, y) => (x[params.sort_by] > y[params.sort_by] ? dir : -dir));
    } else {
      rows.sort((x, y) => (x.date_facture > y.date_facture ? -1 : 1));
    }
    const all = store.data.achats;
    const stats = {
      count_brouillon: all.filter((a) => a.statut === "BROUILLON").length,
      count_valide: all.filter((a) => a.statut === "VALIDE").length,
      montant_total_valide: all
        .filter((a) => a.statut === "VALIDE")
        .reduce((s, a) => s + (a.montant_ttc || 0), 0),
    };
    return paginate(rows, params, { stats });
  });

  route("GET", "/achats/:id", (ctx) => {
    const a = ctx.store.data.achats.find((x) => x.id === ctx.params.id);
    if (!a) httpError(404, "Achat introuvable", "NOT_FOUND");
    return a;
  });

  route("POST", "/achats", (ctx) => {
    const body = ctx.body || {};
    const frn = ctx.store.data.fournisseurs.find((f) => f.id === body.fournisseur_id);
    const id = newId("achat-new");
    const created = {
      id,
      numero_interne: `ACH-2026-${id.slice(-5).toUpperCase()}`,
      fournisseur_id: body.fournisseur_id,
      fournisseur: frn ? { id: frn.id, nom: frn.nom } : null,
      type_piece: body.type_piece || "FACTURE",
      numero_piece: body.numero_piece || "",
      date_facture: body.date_facture || todayYmd(),
      date_reception: body.date_reception || todayYmd(),
      date_echeance: body.date_echeance || dateOffsetYmd(30),
      statut: "BROUILLON",
      montant_ht: 0,
      montant_tva: 0,
      montant_ttc: 0,
      montant_paye: 0,
      reste_a_payer: 0,
      statut_paiement: "NON_PAYE",
      notes: body.notes || "",
      saisi_par_id: body.saisi_par_id || null,
      validee_par_id: null,
      details: [],
      created_at: nowIso(),
      updated_at: nowIso(),
    };
    ctx.store.data.achats.unshift(created);
    ctx.commit();
    return created;
  });

  route("PATCH", "/achats/:id", (ctx) => {
    const a = ctx.store.data.achats.find((x) => x.id === ctx.params.id);
    if (!a) httpError(404, "Achat introuvable", "NOT_FOUND");
    if (a.statut !== "BROUILLON") httpError(400, "Seuls les brouillons sont modifiables", "NOT_DRAFT");
    Object.assign(a, ctx.body || {});
    recomputeAchatTotals(a);
    ctx.commit();
    return a;
  });

  route("DELETE", "/achats/:id", (ctx) => {
    const a = ctx.store.data.achats.find((x) => x.id === ctx.params.id);
    if (!a) httpError(404, "Achat introuvable", "NOT_FOUND");
    if (a.statut !== "BROUILLON") httpError(400, "Seuls les brouillons sont supprimables", "NOT_DRAFT");
    ctx.store.data.achats = ctx.store.data.achats.filter((x) => x.id !== a.id);
    ctx.commit();
    return { success: true };
  });

  route("POST", "/achats/:id/details", (ctx) => {
    const a = ctx.store.data.achats.find((x) => x.id === ctx.params.id);
    if (!a) httpError(404, "Achat introuvable", "NOT_FOUND");
    const lines = ctx.body?.details || [];
    for (const ln of lines) {
      const p = ctx.store.data.produits.find((x) => x.id === ln.produit_id);
      a.details.push({
        id: newId("achat-det"),
        produit_id: ln.produit_id,
        produit: p ? { id: p.id, nom_commercial: p.nom_commercial, ppa: p.ppa, code_barre: p.code_barre, dci: p.dci, forme_pharma: p.forme_pharma, dosage: p.dosage, laboratoire_id: p.laboratoire_id } : null,
        numero_lot: ln.numero_lot || "",
        date_peremption: ln.date_peremption || dateOffsetYmd(365),
        quantite: Number(ln.quantite) || 0,
        quantite_offerte: Number(ln.quantite_offerte) || 0,
        prix_achat_ht: Number(ln.prix_achat_ht) || 0,
        prix_achat_ttc: Number(ln.prix_achat_ttc) || 0,
        remise_pct: Number(ln.remise_pct) || 0,
        tva_pct: Number(ln.tva_pct) || 19,
        montant_ligne_ht: 0,
        montant_ligne_ttc: 0,
      });
    }
    recomputeAchatTotals(a);
    ctx.commit();
    return a;
  });

  route("PATCH", "/achats/:id/details/:detailId", (ctx) => {
    const a = ctx.store.data.achats.find((x) => x.id === ctx.params.id);
    if (!a) httpError(404, "Achat introuvable", "NOT_FOUND");
    const d = a.details.find((x) => x.id === ctx.params.detailId);
    if (!d) httpError(404, "Ligne introuvable", "NOT_FOUND");
    Object.assign(d, ctx.body || {});
    recomputeAchatTotals(a);
    ctx.commit();
    return a;
  });

  route("DELETE", "/achats/:id/details/:detailId", (ctx) => {
    const a = ctx.store.data.achats.find((x) => x.id === ctx.params.id);
    if (!a) httpError(404, "Achat introuvable", "NOT_FOUND");
    a.details = a.details.filter((x) => x.id !== ctx.params.detailId);
    recomputeAchatTotals(a);
    ctx.commit();
    return a;
  });

  route("POST", "/achats/:id/validate", (ctx) => {
    const a = ctx.store.data.achats.find((x) => x.id === ctx.params.id);
    if (!a) httpError(404, "Achat introuvable", "NOT_FOUND");
    if (a.statut !== "BROUILLON") httpError(400, "Déjà validé", "NOT_DRAFT");
    // Create stock lots + bump produit.stock_actuel
    for (const d of a.details) {
      const totalQty = (d.quantite || 0) + (d.quantite_offerte || 0);
      ctx.store.data.stockLots.push({
        id: newId("lot"),
        produit_id: d.produit_id,
        numero_lot: d.numero_lot || `LOT-${a.numero_interne}-${d.id.slice(-3)}`,
        quantite_initiale: totalQty,
        quantite_actuelle: totalQty,
        date_peremption: d.date_peremption,
        date_fabrication: todayYmd(),
        prix_achat_ht: d.prix_achat_ht,
        prix_achat_ttc: d.prix_achat_ttc,
        achat_id: a.id,
        statut: "ACTIF",
        location: "A1",
      });
      const p = ctx.store.data.produits.find((x) => x.id === d.produit_id);
      if (p) p.stock_actuel = (p.stock_actuel || 0) + totalQty;
    }
    a.statut = "VALIDE";
    a.validee_par_id = ctx.store.data.users[0]?.id || null;
    a.validee_at = nowIso();
    a.updated_at = nowIso();
    ctx.commit();
    return a;
  });
}
